import { type MaybeRefOrGetter, ref, toValue } from "vue";
import { API } from "@/api/api";
import i18n from "@/plugins/i18n";
import { Response } from "@/api/client";
import { useError } from "@/composables/useError";
import { useFilters } from "@/composables/useFilters";
import {
  EDITABLE_ORDER_PAYMENT_STATUSES,
  ORDER_PAYMENT_DEDUCTION_TYPES,
  ORDER_STATUSES_WITHOUT_NEW_PAYMENTS,
  OrderPaymentStatus,
  OrderPaymentType,
  OrderStatus,
  TONU_ORDER_STATUSES,
} from "@/constants/order";
import { generateId } from "@/utils/generateId";
import type { CreateOrderPayment, OrderPayment, UpdateOrderPayment } from "@/api/orders/dto/order.dto";
import type { DisableRule } from "@/types/validation";

const t = i18n.global.t;

export function useOrderPayments() {
  const api = new API();
  const { showAPIError } = useError();
  const { price } = useFilters();

  const isLoading = ref(false);

  function createOrderPaymentDraft(type: string | null = null, rate: number | null = null): OrderPayment {
    return { tempId: generateId(), type, rate };
  }

  function isDeductionPaymentType(type: string | null) {
    return ORDER_PAYMENT_DEDUCTION_TYPES.some((deductionType) => deductionType === type);
  }

  function getMandatoryOrderPaymentType(status?: OrderStatus): OrderPaymentType {
    if (status && TONU_ORDER_STATUSES.includes(status)) {
      return OrderPaymentType.Tonu;
    }

    return OrderPaymentType.FlatLinehaul;
  }

  function createMandatoryOrderPayment(payments: OrderPayment[], status?: OrderStatus): OrderPayment[] {
    const mandatoryType = getMandatoryOrderPaymentType(status);

    if (payments.some((payment) => payment.type === mandatoryType)) {
      return payments;
    }

    const rate = status === OrderStatus.waitingTonu ? 0 : null;

    return [...payments, createOrderPaymentDraft(mandatoryType, rate)];
  }

  function createAddOrderPaymentDisableRules(orderStatus: MaybeRefOrGetter<OrderStatus | undefined>): DisableRule[] {
    const status = toValue(orderStatus);

    return [
      {
        disabled: status !== undefined && ORDER_STATUSES_WITHOUT_NEW_PAYMENTS.includes(status),
        reasons: [t("order-payments.add-payment-unavailable")],
      },
    ];
  }

  function createChangeOrderPaymentDisableRules(
    payment: MaybeRefOrGetter<OrderPayment>,
    orderNumber: MaybeRefOrGetter<number | undefined>,
  ): DisableRule[] {
    const { status } = toValue(payment);

    return [
      {
        disabled: status !== undefined && !EDITABLE_ORDER_PAYMENT_STATUSES.includes(status),
        reasons: [getChangeOrderPaymentUnavailableReason(toValue(payment), toValue(orderNumber))],
      },
    ];
  }

  function getChangeOrderPaymentUnavailableReason(payment: OrderPayment, orderNumber: number | undefined): string {
    switch (payment.status) {
      case OrderPaymentStatus.synced:
        return t("order-payments.change-payment-unavailable-synced", { number: orderNumber });
      case OrderPaymentStatus.inherited:
        return t("order-payments.change-payment-unavailable-inherited", {
          number: orderNumber,
          invoice: payment.invoiceId,
        });
      case OrderPaymentStatus.locked:
      default:
        return t("order-payments.change-payment-unavailable-locked", { number: orderNumber });
    }
  }

  async function createOrderPayment(orderId: string, payment: CreateOrderPayment): Promise<OrderPayment | null> {
    isLoading.value = true;

    try {
      const response = await api.orders.createOrderPayment(orderId, payment);

      if (!response.success || !response.data) {
        showAPIError(response, t("order-payments.create-error"));
        return null;
      }

      return response.data;
    } catch (error) {
      console.error(error);
      showAPIError(Response.unknownError(), t("order-payments.create-error"));
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  async function updateOrderPayment(
    orderId: string,
    paymentId: number,
    payment: UpdateOrderPayment,
  ): Promise<OrderPayment | null> {
    isLoading.value = true;

    try {
      const response = await api.orders.updateOrderPayment(orderId, paymentId, payment);

      if (!response.success || !response.data) {
        showAPIError(response, t("order-payments.update-error"));
        return null;
      }

      return response.data;
    } catch (error) {
      console.error(error);
      showAPIError(Response.unknownError(), t("order-payments.update-error"));
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  async function deleteOrderPayment(orderId: string, paymentId: number): Promise<boolean> {
    isLoading.value = true;

    try {
      const response = await api.orders.deleteOrderPayment(orderId, paymentId);

      if (!response.success) {
        showAPIError(response, t("order-payments.delete-error"));
        return false;
      }

      return true;
    } catch (error) {
      console.error(error);
      showAPIError(Response.unknownError(), t("order-payments.delete-error"));
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  function formatOrderPaymentRate(rate: number): string {
    const formatted = price(String(Math.abs(rate)));

    return rate < 0 ? `-${formatted}` : formatted;
  }

  return {
    isLoading,
    deleteOrderPayment,
    formatOrderPaymentRate,
    updateOrderPayment,
    createOrderPayment,
    createAddOrderPaymentDisableRules,
    createChangeOrderPaymentDisableRules,
    createOrderPaymentDraft,
    isDeductionPaymentType,
    getMandatoryOrderPaymentType,
    createMandatoryOrderPayment,
  };
}
